import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TablePagination from '@mui/material/TablePagination';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useUser } from '../../Context/UserContext';
import Chip from '@mui/material/Chip';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// --- API Service Imports ---
import { GetApi, PostApi, PutApi, DeleteApi } from '../../Services/api/ApiCall';

// --- ICONS ---
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BadgeIcon from '@mui/icons-material/Badge';
import BusinessIcon from '@mui/icons-material/Business';
import LockIcon from '@mui/icons-material/Lock';

// --- Dynamic Components Import ---
import DynamicOffcanvas from '../Dynamic/DynamicOffcanvas';
import DynamicTable from '../Dynamic/DynamicTable';
import DynamicModal from '../Dynamic/DynamicModal';
import DynamicField from '../Dynamic/DynamicField';
import API_URLS from '../../Services/api/action';
import InputAdornment from '@mui/material/InputAdornment';

// --- Common Input Styles (Green Border on Focus/Hover, Black Label) ---
const commonInputStyles = {
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2e7d32', // Green hover
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#2e7d32', // Green focus
    borderWidth: '2px'
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#000000', // Black label on focus
    fontWeight: 500
  },
  '& .MuiAutocomplete-inputRoot': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#2e7d32',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#2e7d32',
    },
  }
};

// --- Tab Panel Helper ---
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const OfficerRegister = () => {
  const { token, user, role, authData, userId } = useUser();
  const currentUserRole = role;
  const currentUserId = userId;

  // --- Dynamic Configuration based on Role ---
  const getVisibleTabs = () => {
    if (currentUserRole === 'superadmin') return ['Department', 'District', 'Block'];
    if (currentUserRole === 'department_admin') return ['District', 'Block'];
    if (currentUserRole === 'district_admin') return ['Block'];
    return [];
  };

  const tabLabels = getVisibleTabs();

  const tabToRoleMap = {
    'Department': 'department_admin',
    'District': 'district_admin',
    'Block': 'block_admin'
  };

  const canRegister = ['superadmin', 'department_admin', 'district_admin'].includes(currentUserRole);

  // --- Tab State ---
  const [tabValue, setTabValue] = useState(0);

  // --- Table & Pagination State ---
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  
  // Server-side Pagination State
  const [page, setPage] = useState(0); 
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // --- Form State ---
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    officerName: '', gender: 'Male', mobile: '', email: '',
    department: '', designation: '', role: '', 
    district: null,
    block: null
  });

  // --- Delete Dialog State ---
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  // --- Dropdown Data State ---
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);

  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [blocksLoading, setBlocksLoading] = useState(false);

  // --- Derive Current Tab Info ---
  const currentTabLabel = tabLabels[tabValue];
  const targetRoleName = tabToRoleMap[currentTabLabel];

  // --- Filter Roles based on Current Tab ---
  const filteredRoles = roles.filter(r => r.name === targetRoleName);

  // --- Effects ---
  useEffect(() => {
    if (tabLabels.length > 0) {
      fetchTableData();
    }
  }, [tabValue, page, rowsPerPage, token, currentUserRole]);

  useEffect(() => {
    if (registerOpen) {
      fetchDropdownData();
      if (currentUserRole === 'district_admin' && districts.length === 0) {
        fetchDistricts();
      }
      if (!editingId && currentUserRole === 'district_admin' && authData?.district_id) {
        fetchBlocks(authData.district_id);
      }
    }
  }, [registerOpen, token]);

  useEffect(() => {
    if (registerOpen && !editingId && currentUserRole === 'district_admin' && authData?.district_id && districts.length > 0) {
      const userDistrict = districts.find(d => d.id == authData.district_id);
      if (userDistrict && formData.district?.id !== userDistrict.id) {
        setFormData(prev => ({ ...prev, district: userDistrict }));
      }
    }
  }, [registerOpen, editingId, currentUserRole, authData, districts]);

  useEffect(() => {
    const selectedRoleName = roles.find(r => r.id == formData.role)?.name;
    if (currentUserRole === 'department_admin' && (selectedRoleName === 'block_admin' || selectedRoleName === 'district_admin' ) && districts.length === 0) {
      fetchDistricts();
    }
    if (currentUserRole === 'superadmin' && (selectedRoleName === 'block_admin' || selectedRoleName === 'district_admin' )&& districts.length === 0) {
      fetchDistricts();
    }
  }, [formData.role, roles, currentUserRole]);

  useEffect(() => {
    if (registerOpen && !editingId && filteredRoles.length > 0) {
      if (formData.role !== filteredRoles[0].id) {
        setFormData(prev => ({
          ...prev,
          role: filteredRoles[0].id
        }));
      }
    }
  }, [registerOpen, editingId, filteredRoles]);

  useEffect(() => {
    if (token) {
      fetchDropdownData(); 
    }
  }, [token]);

  useEffect(() => {
    if (tabLabels.length > 0 && roles.length > 0) {
      fetchTableData();
    }
  }, [tabValue, page, rowsPerPage, token, currentUserRole, roles]);

  // --- Handling Editing Data Mapping ---
  useEffect(() => {
    if (!editingRow) return;
    if (dropdownLoading) return;
    if (blocksLoading) return; 

    const findIdByName = (list, name) => {
      const item = list.find(item => item.name === name);
      return item ? item.id : '';
    };

    const deptId = findIdByName(departments, editingRow.department);
    const desigId = findIdByName(designations, editingRow.designation);
    const roleId = findIdByName(roles, editingRow.role);

    let districtObj = null;
    if (editingRow.districtName) {
      districtObj = districts.find(d => d.name === editingRow.districtName || d.District_Name === editingRow.districtName) || null;
    }

    if (districtObj && editingRow.role === 'block_admin') {
      const blockExists = blocks.find(b => 
        (b.name && b.name === editingRow.blockName) || 
        (b.Block_Name && b.Block_Name === editingRow.blockName)
      );

      if (blocks.length === 0 || !blockExists) {
        fetchBlocks(districtObj.id);
      }
    }

    let blockObj = null;
    if (editingRow.blockName) {
      blockObj = blocks.find(b => b.name === editingRow.blockName || b.Block_Name === editingRow.blockName) || null;
    }

    setFormData(prev => ({
      ...prev,
      officerName: editingRow.officerName || '',
      // Handling Gender: Default to 'Male' if missing, but log if data is missing
      gender: editingRow.gender || 'Male',
      mobile: (editingRow.mobile || '').replace(/^\+91/, ''),
      email: editingRow.email || '',
      department: deptId,
      designation: desigId,
      role: roleId,
      district: districtObj,
      block: blockObj
    }));

  }, [editingRow, dropdownLoading, departments, designations, roles, districts, blocks, blocksLoading]);

  // --- Data Fetching Functions ---
  const fetchTableData = async () => {
    setTableLoading(true);
    try {
      const currentTabLabel = tabLabels[tabValue];
      const targetRoleName = tabToRoleMap[currentTabLabel];
      const targetRole = roles.find(r => r.name === targetRoleName);
      
      if (!targetRole) {
        setTableLoading(false);
        return;
      }
      
      const roleId = targetRole.id; 
      const apiPage = page + 1;
      const response = await GetApi(`${API_URLS.Officer}?page=${apiPage}&limit=${rowsPerPage}&role_id=${roleId}`, token);
      
      // Check for Backend SQL Error (e.g. Unknown column 'td.district_id')
      if (response && response.error) {
        console.error("Backend Error:", response.error);
        toast.error(`Database Error: ${response.error}`);
        setTableData([]);
        setTotalItems(0);
      } else if (Array.isArray(response)) {
        setTableData(response);
        setTotalItems(response.length);
      } else {
        setTableData(response.data || []);
        setTotalItems(response.pagination?.totalItems || 0);
      }
    } catch (e) {
      console.error("Error fetching table data", e);
      toast.error("Failed to load officer data.");
      setTableData([]);
      setTotalItems(0);
    } finally {  
      setTableLoading(false);
    }
  };

      const fetchDropdownData = async () => {
    setDropdownLoading(true);
    try {
      const [deptRes, desigRes, rolesRes] = await Promise.all([
        GetApi(`${API_URLS.Department}`, token),
        GetApi(`${API_URLS.Degisnation}`, token),
        GetApi(`${API_URLS.Role}`, token)
      ]);

      const formatData = (data) => {
        if (!data) return [];
        return data.map(d => ({
          id: d._id || d.id || d.name || d,
          name: d.name || d.departmentName || d.designationName || d.roleName || d
        }));
      };

      let formattedRoles = formatData(rolesRes);

      if (currentUserRole === 'superadmin') {
        formattedRoles = formattedRoles.filter(r => ['department_admin', 'district_admin', 'block_admin'].includes(r.name));
      } else if (currentUserRole === 'department_admin') {
        formattedRoles = formattedRoles.filter(r => ['district_admin', 'block_admin'].includes(r.name));
      } else if (currentUserRole === 'district_admin') {
        formattedRoles = formattedRoles.filter(r => r.name === 'block_admin');
      } else {
        formattedRoles = [];
      }
      
      setDepartments(formatData(deptRes));
      setDesignations(formatData(desigRes));
      setRoles(formattedRoles);

    } catch (e) {
      console.error("Error fetching dropdowns", e);
      toast.error("Failed to load dropdown data.");
    } finally {
      setDropdownLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const res = await GetApi(`${API_URLS.GetDistricts}`, token);
      setDistricts(res || []);
    } catch (e) {
      console.error("Error fetching districts", e);
    }
  };

  const fetchBlocks = async (districtId) => {
    if (!districtId) return;
    setBlocksLoading(true);
    setBlocks([]);
    try {
      const res = await GetApi(`${API_URLS.GetBlocks}?district_id=${districtId}`, token);
      setBlocks(res || []);
    } catch (e) {
      console.error("Error fetching blocks", e);
    } finally {
      setBlocksLoading(false);
    }
  };

  // --- Pagination & Tab Handlers ---
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0); 
  };

  // --- Form Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = name === 'mobile' ? value.replace(/[^\d]/g, '').slice(0, 10) : value;

    if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
    }

    if (name === 'role') {
      setFormData(prev => ({ ...prev, role: finalValue, district: null, block: null }));
    } else {
      setFormData(prev => ({ ...prev, [name]: finalValue }));
    }
  };

  const handleAutocompleteChange = (name, value) => {
    if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'district') {
      const selectedRoleName = roles.find(r => r.id == formData.role)?.name;
      if (selectedRoleName === 'block_admin' && value?.id) {
        fetchBlocks(value.id);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      officerName: '', gender: 'Male', mobile: '', email: '',
      department: '', designation: '', role: '', 
      district: null, block: null
    });
    setErrors({});
    setEditingId(null);
    setEditingRow(null);
  };

 const validate = () => {
    let newErrors = {};
    if (!formData.officerName) newErrors.officerName = "Full Name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";
    
    if (!formData.mobile) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
      newErrors.mobile = "Invalid 10-digit mobile number";
    }
    
    if (!formData.role) newErrors.role = "Role is required";
    if (!formData.designation) newErrors.designation = "Designation is required";
    
    if (currentUserRole === 'superadmin' && !formData.department) newErrors.department = "Department is required";
    
    const selectedRoleName = roles.find(r => r.id == formData.role)?.name;
    if (currentUserRole !== 'district_admin' && (selectedRoleName === 'district_admin' || selectedRoleName === 'block_admin') && !formData.district) {
        newErrors.district = "District is required";
    }
    if (selectedRoleName === 'block_admin' && !formData.block) {
        newErrors.block = "Block is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!validate()) return;

    const payload = {
      email: formData.email,
      officername: formData.officerName,
      gender: formData.gender,
      mobile: formData.mobile ? `+91${formData.mobile}` : '',
      designation: formData.designation,
      role: formData.role,
      department: currentUserRole === 'superadmin' ? formData.department : authData?.department_id,
      district_id: currentUserRole === 'district_admin' ? authData?.district_id : (formData.district?.id || null),
      block_id: formData.block?.id || null,
      created_by: editingId ? undefined : currentUserId,
      created_at: editingId ? undefined : new Date().toISOString()
    };

    try {
      if (editingId) {
        await PutApi(`${API_URLS.Officer}${editingId}`, {}, payload, token);
        toast.success("Update Successful!");
      } else {
        await PostApi(`${API_URLS.OfficerRegistration}`, payload, token);
        toast.success("Registration Successful!");
      }
      setRegisterOpen(false);
      resetForm();
      fetchTableData();
    } catch (err) {
      console.error("Operation Failed", err);
      
      // Extract error message from response
      const responseData = err?.response?.data;
      const errMsg = responseData?.message || responseData?.error || err?.message || "Operation Failed";
      
      // Handle specific error cases with appropriate toast notifications
      if (errMsg.toLowerCase().includes('mobile') && errMsg.toLowerCase().includes('already')) {
        // Specific handling for mobile number already exists
        toast.error("Mobile number already registered. Please use a different number.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else if (errMsg.toLowerCase().includes('exist')) {
        // Generic exists error
        toast.error("Mobile number already registered. Please use a different number.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else if (errMsg.toLowerCase().includes('invalid reference')) {
        // Foreign key constraint error
        toast.error("Mobile number already registered. Please use a different number.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      } else {
        // Generic error
        toast.error("Mobile number already registered. Please use a different number.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false, 
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        });
      }
    }
  };

  const handleEdit = (row) => {
    setEditingRow(row);
    setEditingId(row.id);
    setRegisterOpen(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteDialog({ open: true, id: row.id });
  };

  const handleConfirmDelete = async () => {
    try {
      await DeleteApi(`${API_URLS.Officer}${deleteDialog.id}`,{}, token);
      toast.success("Deleted Successfully");
      fetchTableData();
    } catch (err) {
      toast.error("Delete Failed");
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  // --- RENDER HELPERS ---
  const renderFormSection = (title, icon, children) => (
    <Box sx={{ 
      mb: 3, 
      p: 2.5, 
      borderRadius: '12px', 
      border: '1px solid #e0e0e0', 
      bgcolor: '#fff',
      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          p: 1, 
          borderRadius: '8px', 
          bgcolor: '#e8f5e9', 
          color: '#1b5e20' 
        }}>
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} color="#1b5e20" textTransform="uppercase" letterSpacing={1}>
          {title}
        </Typography>
      </Box>
      <Grid container spacing={3}>
        {children}
      </Grid>
    </Box>
  );

  const offcanvasActions = (
    <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
      <Button 
        variant="outlined" 
        fullWidth 
        color="error" 
        onClick={() => { setRegisterOpen(false); resetForm(); }} 
        sx={{ fontWeight: 700, py: 1.5, borderRadius: '8px', textTransform: 'none' }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        fullWidth
        sx={{ 
          bgcolor: '#1b5e20', 
          color: '#fff', 
          fontWeight: 700, 
          py: 1.5, 
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(27, 94, 32, 0.2)',
          '&:hover': { bgcolor: '#004d16' } 
        }}
        onClick={handleFormSubmit}
      >
        {editingId ? "Update Officer" : "Create Account"}
      </Button>
    </Box>
  );

  const currentSelectedRoleName = roles.find(r => r.id == formData.role)?.name;

  const getColumns = () => {
    const cols = [
      { field: 'sno', headerName: 'S.No', render: (row) => (page * rowsPerPage) + tableData.findIndex(r => r.id === row.id) + 1 },
      { field: 'officerName', headerName: 'Officer Name' },
      { field: 'email', headerName: 'Email' },
      { field: 'mobile', headerName: 'Phone Number', render: (row) => row.mobile || '-' },
    ];
    if (currentUserRole === 'superadmin') cols.push({ field: 'department', headerName: 'Department' });
    if (currentTabLabel === 'District') cols.push({ field: 'districtName', headerName: 'District' });
    if (currentTabLabel === 'Block') {
      cols.push({ field: 'districtName', headerName: 'District' });
      cols.push({ field: 'blockName', headerName: 'Block' });
    }
    cols.push({ 
      field: 'role', 
      headerName: 'Role', 
      render: (row) => row.role ? <Chip label={row.role} size="small" sx={{ bgcolor: '#e8f5e9', color: '#1b5e20', fontWeight: 700 }} /> : '-'
    });
    cols.push({ field: 'createdBy', headerName: 'Created By' });
    cols.push({ field: 'createdAt', headerName: 'Created At', render: (row) => row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-' });
    return cols;
  };

  if (!canRegister) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6">Access Denied</Typography></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f4f7f5', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />

      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={4}>
        <Box>
          <Typography variant="h4" sx={{ color: '#1b5e20', fontWeight: 900 }}>Officer Directory</Typography>
          <Typography variant="body1" color="text.secondary">
            Manage system administrators and regional field officers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => { resetForm(); setRegisterOpen(true); }}
          sx={{ bgcolor: '#1b5e20', px: 3, py: 1, borderRadius: '8px', fontWeight: 700, '&:hover': { bgcolor: '#005500' } }}
        >
          Register New Officer
        </Button>
      </Box>

      {tabLabels.length > 0 && (
        <Paper sx={{ width: '100%', mb: 2, p: 0, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
          <Box sx={{ bgcolor: '#fff', px: 3, pt: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': { fontWeight: 700, minHeight: '64px' },
                '& .Mui-selected': { color: '#1b5e20 !important' },
                '& .MuiTabs-indicator': { height: '3px', borderRadius: '3px', backgroundColor: '#1b5e20' }
              }}
            >
              {tabLabels.map((label) => <Tab key={label} label={label} />)}
            </Tabs>
          </Box>
          <Divider />
          <Box sx={{ p: 3 }}>
            <TabPanel value={tabValue} index={tabValue}>
              <DynamicTable 
                data={tableData}
                columns={getColumns()}
                loading={tableLoading}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                disablePagination={true} 
              />
              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ mt: 2 }}
              />
            </TabPanel>
          </Box>
        </Paper>
      )}

      {/* FORM UI */}
      <DynamicOffcanvas
        open={registerOpen}
        onClose={() => { setRegisterOpen(false); resetForm(); }}
        title={editingId ? "Update Profile" : "Create New Officer Account"}
        width="650px"
        actions={offcanvasActions}
      >
        <Box sx={{ bgcolor: '#f9f9f9', p: 1, borderRadius: '12px' }}>
          
          {renderFormSection("Personal Information", <BadgeIcon fontSize="small" />,
            <>
              <Grid item xs={12} sm={6}>
                <DynamicField 
                  field={{ name: 'officerName', label: 'Full Name', required: true }}
                  value={formData.officerName}
                  onChange={handleInputChange}
                  error={Boolean(errors.officerName)}
                  helperText={errors.officerName}
                  sx={commonInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                 <DynamicField 
                  field={{ name: 'gender', label: 'Gender', type: 'select', required: true }}
                  value={formData.gender}
                  onChange={handleInputChange}
                  options={[{ id: 'Male', name: 'Male' }, { id: 'Female', name: 'Female' }]}
                  sx={commonInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DynamicField 
                  field={{ 
                    name: 'mobile', label: 'Mobile Contact', type: 'tel',
                    InputProps: { 
                      startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                      maxLength: 10 
                    }
                  }}
                  value={formData.mobile}
                  onChange={handleInputChange}
                  error={Boolean(errors.mobile)}
                  helperText={errors.mobile}
                  sx={commonInputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                 <DynamicField 
                  field={{ name: 'email', label: 'Email Address', type: 'email', required: true }}
                  value={formData.email}
                  onChange={handleInputChange}
                  error={Boolean(errors.email)} 
                  helperText={errors.email}
                  sx={commonInputStyles}
                />
              </Grid>
            </>
          )}

          {renderFormSection("Work Assignment", <BusinessIcon fontSize="small" />,
            <>
              <Grid item xs={12} sm={6}>
                 <DynamicField 
                  field={{ name: 'role', label: 'Assign Role', type: 'select', required: true }}
                  value={formData.role}
                  onChange={handleInputChange}
                  options={filteredRoles} 
                  loading={dropdownLoading}
                  error={Boolean(errors.role)}
                  helperText={errors.role}
                  sx={commonInputStyles}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DynamicField 
                  field={{ name: 'designation', label: 'Designation', type: 'select', required: true }}
                  value={formData.designation}
                  onChange={handleInputChange}
                  options={designations}
                  loading={dropdownLoading}
                  error={Boolean(errors.designation)}
                  helperText={errors.designation}
                  // sx={commonInputStyles}
                />
              </Grid>

              {currentUserRole === 'superadmin' && (
                 <Grid item xs={12}>
                   <DynamicField 
                    field={{ name: 'department', label: 'Assign Department', type: 'select', required: true }}
                    value={formData.department}
                    onChange={handleInputChange}
                    options={departments}
                    loading={dropdownLoading}
                    error={Boolean(errors.department)}
                    helperText={errors.department}
                    //  sx={{
                    //     '& .MuiOutlinedInput-root': {
                    //       // minHeight: 56,
                    //       borderRadius: '8px',
                    //       // maxHeight: 10,

                    //       // default border
                    //       '& fieldset': {
                    //         // borderColor: 'green',
                    //     },

                    //     // hover
                    //     '&:hover fieldset': {
                    //       borderColor: 'darkgreen',
                    //     },

                    //     // focused
                    //     '&.Mui-focused fieldset': {
                    //       borderColor: 'green',
                    //       borderWidth: 2,
                    //     },
                    //   }}}
                    // sx={commonInputStyles}
                  />
                </Grid>
              )}

              {['district_admin', 'block_admin'].includes(currentSelectedRoleName) && (
                <Grid item xs={12} sm={currentSelectedRoleName === 'block_admin' ? 6 : 12}>
                  {currentUserRole === 'district_admin' ? (
                    <TextField
                      label="District Jurisdiction"
                      value={formData.district?.District_Name || formData.district?.name || 'Loading...'}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                      fullWidth
                       sx={{
                        '& .MuiOutlinedInput-root': {
                          // minHeight: 56,
                          borderRadius: '8px',
                          maxHeight: 10,

                          // default border
                          '& fieldset': {
                            // borderColor: 'green',
                        },

                        // hover
                        '&:hover fieldset': {
                          borderColor: 'darkgreen',
                        },

                        // focused
                        '&.Mui-focused fieldset': {
                          borderColor: 'green',
                          borderWidth: 2,
                        },
                      }}}
                      // sx={{
                      //   ...commonInputStyles,
                      //   '& .MuiInputBase-root': { height: 56, borderRadius: '8px', backgroundColor: '#f8f9fa' },
                      //   '& .MuiInputBase-input': { fontWeight: 600 }
                      // }}
                    />
                  ) : (
                    <Autocomplete
                      fullWidth
                      // multiple={false} // Not needed, Autocomplete is single select by default
                      options={districts}
                      getOptionLabel={(option) => option.name || option.District_Name || ''}
                      value={formData.district}
                      onChange={(e, val) => handleAutocompleteChange('district', val)}
                                    sx={{
                        '& .MuiOutlinedInput-root': {
                          // minHeight: 56,
                          borderRadius: '8px',
                          maxHeight: 10,

                          // default border
                          '& fieldset': {
                            // borderColor: 'green',
                        },

                        // hover
                        '&:hover fieldset': {
                          borderColor: 'darkgreen',
                        },

                        // focused
                        '&.Mui-focused fieldset': {
                          borderColor: 'green',
                          borderWidth: 2,
                        },
                      }}}
                      slotProps={{
                        popper: {
                          sx: {
                            width: '200px !important', 
                            '& .MuiPaper-root': {
                              borderRadius: '8px',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            }
                          }
                        },
                        paper: {
                          sx: {
                            '& .MuiAutocomplete-listbox': {
                              padding: '8px',
                              '& .MuiAutocomplete-option': {
                                minHeight: 38,
                                borderRadius: '6px',
                                fontSize: '1rem',
                              }
                            }
                          }
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select District"
                          required
                          variant="outlined"
                          error={Boolean(errors.district)}
                          helperText={errors.district}
                          sx={{ '& .MuiOutlinedInput-root': { minHeight: 56, borderRadius: '8px' } }}
                        />
                      )}
                    />
                  )}
                </Grid>
              )}

              {currentSelectedRoleName === 'block_admin' && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    // fullWidth
                    options={blocks}
                    getOptionLabel={(option) => option.name || option.Block_Name || ''}
                    value={formData.block}
                    onChange={(e, val) => handleAutocompleteChange('block', val)}
                    loading={blocksLoading}
                    disabled={currentUserRole !== 'district_admin' && !formData.district}
                    // sx={commonInputStyles}
                     sx={{
                        '& .MuiOutlinedInput-root': {
                          // minHeight: 56,
                          borderRadius: '8px',
                          maxHeight: 10,

                          // default border
                          '& fieldset': {
                            // borderColor: 'green',
                        },

                        // hover
                        '&:hover fieldset': {
                          borderColor: 'darkgreen',
                        },

                        // focused
                        '&.Mui-focused fieldset': {
                          borderColor: 'green',
                          borderWidth: 2,
                        },
                      }}}
                    slotProps={{
                      popper: {
                        sx: {
                          width: '400px !important', 
                          '& .MuiPaper-root': {
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          }
                        }
                      },
                      paper: {
                        sx: {
                          '& .MuiAutocomplete-listbox': {
                            padding: '8px',
                            '& .MuiAutocomplete-option': {
                              minHeight: 48,
                              borderRadius: '6px',
                            }
                          }
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Block"
                        required
                        variant="outlined"
                        error={Boolean(errors.block)}
                        helperText={errors.block}
                        sx={{ '& .MuiOutlinedInput-root': { minHeight: 56, borderRadius: '8px' } }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {blocksLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
             
            </>
          )}
        </Box>
      </DynamicOffcanvas>

      <DynamicModal
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        confirmText="Confirm Delete"
        contentText="Are you sure you want to remove this officer? This action will permanently revoke their system access."
      />
    </Box>
  );
};

export default OfficerRegister;